import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { Connector } from '@/types/core/connector';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * Find a specific connector by ID.
 * Returns a react-query query.
 */
export function useFindConnector(
  tenantId?: UUID,
  connectorId?: UUID
): UseQueryResult<Connector | null, Error> {
  return useQuery<Connector | null, Error>({
    queryKey: connectorQueryKeys.detail(tenantId, connectorId),
    queryFn: async () => {
      if (isEmptyUUID(tenantId) || isEmptyUUID(connectorId)) return null;
      const result = await connectorService.findConnector(
        tenantId!,
        connectorId!
      );
      if (!result) throw new Error('Connector not found');
      return result;
    },
    enabled: !isEmptyUUID(tenantId) && !isEmptyUUID(connectorId),
    retry: 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
    // Connector details are stable during a session. Override the aggressive
    // global defaults (`staleTime: 0` + `refetchOnMount: 'always'`) so that
    // remounting this hook — e.g. as virtualized log rows scroll in and out of
    // view — serves from cache instead of refiring a request per row.
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}
